import { useEffect, useState } from "react";
import { Calendar, Clock, Phone, PhoneCall, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type CallLog = Tables<"call_logs">;

const BROWSER_HANDOFF_DIRECTION = "browser_outbound";

interface CallLogsProps {
  agentId: string;
}

const CallLogs = ({ agentId }: CallLogsProps) => {
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (agentId) {
      fetchCallLogs();
    }
  }, [agentId]);

  const fetchCallLogs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from("call_logs")
        .select("*")
        .eq("agent_id", agentId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setCallLogs(data || []);
    } catch (err: any) {
      setError(err.message || "Failed to load call logs");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (duration: number | null) => {
    if (!duration) return "N/A";
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCaller = (call: CallLog) => {
    if (call.direction === BROWSER_HANDOFF_DIRECTION || call.from_number?.startsWith("client:")) {
      return "Website visitor (live handoff)";
    }
    return call.from_number || "Unknown";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "text-green-600 bg-green-50";
      case "failed":
        return "text-red-600 bg-red-50";
      case "busy":
        return "text-yellow-600 bg-yellow-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-500">Loading call logs...</span>
      </div>
    );
  }

  if (callLogs.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <PhoneCall className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 mb-2">No call logs found</p>
        <p className="text-sm text-gray-400">Call logs will appear here once your genie receives calls</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">All Calls ({callLogs.length})</h3>
      <div className="space-y-3">
        {callLogs.map((call) => (
          <div key={call.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center">
                <PhoneCall className="h-4 w-4 text-gray-400 mr-2" />
                <span className="font-medium text-gray-900">Call {call.call_sid.slice(-8)}</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(call.status)}`}>
                {call.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">From:</span>
                <span className="ml-1">{formatCaller(call)}</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">To:</span>
                <span className="ml-1">{call.to_number || "Unknown"}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Time:</span>
                <span className="ml-1">{formatDate(call.start_time)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-gray-600">Duration:</span>
                <span className="ml-1">{formatDuration(call.duration)}</span>
              </div>
            </div>

            {call.transcription && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600 font-medium mb-1">Transcription:</p>
                <p className="text-sm text-gray-700">{call.transcription}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CallLogs;
